import CompositionResult from '../modules/account-connector/compositionResult';
import messages from '../modules/account-connector/serviceMessages';
import proofTypes from '../modules/account-connector/components/proof-of-ownership/proofTypes';
import API from './apiKeys';

// Service Class
export class ApiService {

    logger;

    constructor(logService) {
        this.logger = logService;
    }

    async validateAccountInformation(request, rewardsKey) {
        let initialRequest = new CompositionResult(this.logger, request, rewardsKey);

        const authResult = await this.authorize(initialRequest);
        this.logger.logDebug('validate.authorize');

        const shopResult = await this.getShopOwner(authResult);
        this.logger.logDebug('validate.getShopOwner');

        const ready = shopResult.readyToAssociate(shopResult);
        this.logger.logDebug('validate.readyToAssociate');

        this.logger.logDebug(`ready for association ${ready}`);

        if (ready) {
            return await this.createAssociation(shopResult);
        }
        return shopResult;
    }

    async authorize(composition) {

        const url = `${API.API_BASE}${API.AUTH_LOGIN}`;
        let status = 200;
        // notice the double await ...
        const response = await (await (fetch(url, {
            method: API.POST,
            headers: { 'Content-Type': API.JSON_CONTENT_TYPE },
            body: JSON.stringify({
                'username': composition.request.userName,
                'password': composition.request.password
            })
        }).then(res => {
            status = res.status;
            return res.json();
        }).catch(err => {
            this.logger.logError(err);
        })));
        if (status === 200 && response) {
            composition = {
                ...composition,
                tokens: response.token,
                retryCount: 0,
                shopId: response.data.shopId,
                networkStatus: { ...composition.networkStatus, authorization: status }
            };
        } else {
            composition = {
                ...composition,
                tokens: { ...composition.tokens, access_token: null },
                networkStatus: { ...composition.networkStatus, authorization: status },
                validationErrors: [...composition.validationErrors,
                ...[messages.AUTHENTICATION_FAILED]]
            };
        }
        this.logger.logTrace(`API_SERVICE-authorize: ${CompositionResult.logStatus(composition)}`);
        return composition;
    }

    async refreshAuth(composition) {
        const url = `${API.API_BASE}${API.AUTH_REFRESH}`;
        let status = 200;
        // notice the double await ...
        const response = await (await (fetch(url, {
            method: API.POST,
            headers: { 'Content-Type': API.JSON_CONTENT_TYPE },
            body: JSON.stringify({
                'refreshToken': composition.refresh_token
            })
        }).then(res => {
            status = res.status;
            return res.json();
        }).catch(err => {
            this.logger.logError(err);
        })));

        if (status === 200 && response) {
            composition = {
                ...composition,
                tokens: response.token,
                retryCount: 0,
                shopId: response.data.shopId,
                networkStatus: { ...composition.networkStatus, authorization: 200 }
            };
            this.logger.logDebug(composition);
        } else {
            composition = {
                ...composition,
                retryCount: 1,
                tokens: { ...composition.tokens, access_token: null },
                networkStatus: { ...composition.networkStatus, authorization: 401 },
                validationErrors: [...composition.validationErrors,
                ...[messages.AUTHENTICATION_FAILED]]
            };
        }
        this.logger.logTrace(`API_SERVICE-refreshAuth: ${CompositionResult.logStatus(composition)}`);
        return composition;
    }

    /*         
        [SwaggerResponse(200,"Successfully retrieved",typeof(ShopOwnerResponse))]
        [SwaggerResponse(404,"The specified shop owner couldn't be found.")]
        [SwaggerResponse(500,"An unexpected error occurred while processing.")] 
    */
    async getShopOwner(composition) {
        if (composition.validationErrors.length > 0) {
            return composition;
        }

        let url;
        // assuming that it's passed validation by the time it gets here
        if (composition.request.proofType === proofTypes.LAST_FOUR_CREDIT_CARD) {
            url = `${API.API_BASE}${API.SHOP_LAST_FOUR_CREDIT_CARD}${composition.request.proof}`;
        } else {
            url = `${API.API_BASE}${API.SHOP_CMA_NUMBER}${composition.request.proof}`;
        }
        let status;
        const response = await (await (fetch(url, {
            method: API.GET,
            withCredentials: true,
            credentials: 'include',
            headers: {
                'Authorization': `${API.BEARER}${composition.tokens.access_token}`,
                'Content-Type': API.JSON_CONTENT_TYPE
            }
        }).then(res => {
            status = res.status;
            if (status === 200) {
                // 404 and 401 don't return json objects.
                return res.json();
            }
        }).catch(err => {
            // try to reauth
            this.logger.logError(err);
        })));

        if (status === 404) {
            // we authenticated successfully to the server, but are not 
            // authorized to perform this action.. punt
            composition = {
                ...composition,
                validationErrors: [messages.INVALID_IDENTIFIX_CUSTOMER_NUMBER],
                networkStatus: { ...composition.networkStatus, shop: 404 }
            }
        } else if (status === 401) {
            // retry auth?
            if (composition.retryCount === 0) {
                composition = await this.refreshAuth(composition);
                if (composition.isAuthorized()) {
                    return await this.getShopOwner(composition);
                }
            }
        } else {
            composition = {
                ...composition,
                shopId: response.shopId,
                isShopOwner: response.isShopOwner || false,
                networkStatus: { ...composition.networkStatus, shop: status }
            }
        }
        return composition;
    }

    /*
        [SwaggerResponse(201, "Successfully saved")]
        [SwaggerResponse(400, "There was an error in the format of the request.", typeof(ErrorResponse))]
        [SwaggerResponse(403, "There already exists a profile key for this shop.", typeof(ErrorResponse))]
        [SwaggerResponse(404, "The specified shop couldn't be found.")]
        [SwaggerResponse(409, "A resolvable conflict occurred. Wait a couple seconds, then retry.")]
        [SwaggerResponse(500, "An unexpected error occurred while processing.")]
    */
    async createAssociation(composition) {
        if (composition.validationErrors.length > 0) {
            return composition;
        }
        const url = `${API.API_BASE}${API.SHOP_PROFILE_KEY}${composition.shopId}/${API.PROFILE_CREATE}`;
        let apiResult;
        let status = 201;
        // notice the double await ...
        await (await (
            fetch(url, {
                method: API.POST,
                withCredentials: true,
                credentials: 'include',
                headers: {
                    'Authorization': `${API.BEARER}${composition.tokens.access_token}`,
                    'Content-Type': API.JSON_CONTENT_TYPE
                },
                body: JSON.stringify({
                    "shopId": composition.shopId,
                    "rewardsKey": composition.rewardsKey
                })
            }).then(res => {
                status = res.status;
                apiResult = res.json();
                return apiResult;
            }).catch(err => {
                this.logger.logError(err);
            })
        ));

        if (status === 201 || status === 200) {
            composition = {
                ...composition,
                networkStatus: { ...composition.networkStatus, created: status }
            }
        } else {
            const validationErrors = [...composition.validationErrors];
            if (apiResult !== null && apiResult !== undefined) {
                await apiResult.then(result => {
                    validationErrors.push(result);
                });
            }

            composition = {
                ...composition,
                validationErrors: validationErrors,
                networkStatus: { ...composition.networkStatus, created: status }
            }
        }
        this.logger.logTrace(`API_SERVICE-createAssociation: ${composition.success(composition)}`);
        return composition;
    }
}
export default ApiService; 


// WEBPACK FOOTER //
// ./src/services/apiService.js